import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import LandingMain from "../../../components/Layouts/Setting/MainLayout";
import LivisBreadcrumb from "../../../components/Breadcrumb/LivisBreadcrumb";
import {
  Box,
  Button,
  Grid,
  Typography,
  Select,
  MenuItem,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Menu,
  OutlinedInput,
  InputLabel,
} from "@mui/material";
import { LivisTabs } from "../../../components/Tabs";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import useDocumentTitle from "../../../helpers/useDocumentTitle";
import { livisAlert } from "../../../helpers/alert";
import { useQueryClient } from "@tanstack/react-query";
import {
  useOrgComplianceList,
  useTermsRepositoryList,
  useUploadPhysicalMsa,
  useCreateNewTermsVersion,
  useUpdateTermsVersion,
  usePublishTermsVersion,
  useDeleteTermsVersion,
  useDuplicateTermsVersion,
  useDeactivateTermsVersion,
  useTermsHistory,
  useMsaDomains,
  useAllDomains
} from "../hooks/queries/useTerms";
import LivisModal from "../../../components/Modals/LivisModal";
import LivisGeneralTable, { Column } from "../../../components/Table/LivisGeneralTable";
import { LivisPagination } from "../../../components/Pagination";
import AuditLogSkeleton from "../../../components/Loader/Skeleton/AuditLogSkeleton";
import { UploadIconBlue, CreateNew } from "../../../components/Icons/LivisIcons";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import LivisCalendarPicker from "./LivisCalendarPicker";
import { convertToLocalDateTimeWithSecs } from "../../../helpers/utils";
import {checkUniqueVersionAPI } from "../../../services/terms";
import debounce from "lodash/debounce";

import "../styles/terms-and-condition.scss";
import NoDataTemplate from "../../../components/Layouts/NoData/NoDataTemplate";
import LivisTourWrapper, { type Step } from "../../../components/Tour/LivisTourWrapper";
import { LivisTourIcon } from "../../../components/Icons/LivisIcons";
import LivisFloatingButton from "../../../components/Form/Button/LivisFloatingButton";

// ─── Real API shapes ──────────────────────────────────────────────────────────

interface OrgComplianceItem {
  id: string;
  organization: string;
  legal_entity_name: string | null;
  contract_type: "clickwrap" | "signed_msa" | null;
  entity_bound_at: string | null;
  entity_bound_by_email: string | null;
  entity_terms_version: string | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by_email: string | null;
  terms_status: "accepted" | "declined" | "pending";
  msa_configured?: boolean;
  msa_not_configured?: boolean;
  clickwrap_terms_configured?: boolean;
  active_clickwrap_version?: string | null;
  active_msa_terms?: {
    id: string;
    organization: string;
    version: string;
    effective_date: string;
    title: string;
    document_url: string;
    document_hash: string;
    scenario: string;
    change_type: string;
    requires_reacceptance: boolean;
    is_active: boolean;
    created_at: string;
    created_by_email: string;
    notes: string | null;
  } | null;
}

interface TermsVersionItem {
  id: string;
  version: string;
  effective_date: string;
  title: string;
  document_url: string;
  document_hash: string;
  change_type: "minor" | "material";
  requires_reacceptance: boolean;
  scenario: "clickwrap" | "msa_acknowledgment";
  is_active: boolean;
  created_at: string;
  created_by_email: string;
  notes: string | null;
  updated_at: string | null;
  updated_by_email: string | null;
}

interface HistoryEventItem {
  id: string;
  action: "accepted" | "declined";
  user_email: string;
  user_full_name: string;
  organization: string;
  legal_entity_name: string;
  is_org_admin: boolean;
  terms_version: string;
  screen_type: string;
  button_text: string;
  acceptance_method: string;
  contract_type: string;
  created_at: string;
  request_ip: string | null;
}

interface MsaDomainItem {
  organization: string;
  legal_entity_name: string | null;
  contract_type: string;
  msa_configured: boolean;
  active_msa_version: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getTermsStatusBadgeStyles = (status: OrgComplianceItem["terms_status"]) => {
  switch (status) {
    case "accepted":  return { backgroundColor: "#d1fae5", color: "#065f46" };
    case "pending":   return { backgroundColor: "#ffedd5", color: "#9a3412" };
    case "declined":  return { backgroundColor: "#fee2e2", color: "#991b1b" };
    default:          return { backgroundColor: "#e2e8f0", color: "#475569" };
  }
};

const getTermsStatusLabel = (status: OrgComplianceItem["terms_status"]) => {
  switch (status) {
    case "accepted": return "Up-to-date";
    case "pending":  return "Pending";
    case "declined": return "Declined";
    default:         return status;
  }
};

// ─── Lightweight error-aware field wrappers ────────────────────────────────
// This page manages its modal form state with plain useState, not
// react-hook-form, so the shared InputField/LivisFormDropDownSelect
// components (which require RHF's register/control) don't fit without an
// awkward shim. These two small wrappers apply the exact same custom error
// classes (livis-input-field-error / livis-dropdown-select-error) directly
// off plain value/onChange/error/helperText props, so the look matches the
// rest of the app with the smallest possible footprint.

const ErrorTextField = ({
  label,
  value,
  onChange,
  error,
  helperText,
  placeholder,
  type = "text",
  disabled = false,
}: {
  label: string;
  value: any;
  onChange: (e: any) => void;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
}) => (
  <>
    <TextField
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      type={type}
      disabled={disabled}
      fullWidth
      variant="standard"
      className={`livis-input-field${error ? " livis-input-field-error" : ""}`}
      label={label}
      error={!!error}
      InputLabelProps={{ className: "livis-input-field-component-label", shrink: true }}
    />
    <small className="livis-text-danger">{helperText || ""}</small>
  </>
);

const ErrorSelect = ({
  label,
  value,
  onChange,
  error,
  helperText,
  children,
  displayEmpty,
}: {
  label: string;
  value: any;
  onChange: (e: any) => void;
  error?: boolean;
  helperText?: string;
  children: React.ReactNode;
  displayEmpty?: boolean;
}) => (
  <div className={`livis-form-dropdown-select${error ? " livis-dropdown-select-error" : ""}`}>
    <FormControl
      variant="standard"
      fullWidth
      className={`livis-dropdown-select${error ? " livis-dropdown-select-error" : ""}`}
      error={!!error}
    >
      <InputLabel className="livis-dropdown-label" shrink error={!!error}>
        {label}
      </InputLabel>
      <Select
        value={value}
        onChange={onChange}
        displayEmpty={displayEmpty}
        IconComponent={(props: any) => <ExpandMoreIcon {...props} />}
        error={!!error}
      >
        {children}
      </Select>
    </FormControl>
    <small className="livis-text-danger livis-dropdown-error">{helperText || ""}</small>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

const TermsAndConditionsSettings = () => {
  useDocumentTitle("Terms & Conditions Settings");
  const { t } = useTranslation();

  const landing_page_props = {
    livis_logo: process.env.PUBLIC_URL + "/image/icons/lincode_livis.svg",
    livis_hamburger: process.env.PUBLIC_URL + "/image/icons/hamburger.svg",
    search_icon: process.env.PUBLIC_URL + "/image/icons/search.svg",
    setting_icon: process.env.PUBLIC_URL + "/image/icons/settings.svg",
    setting_icon_bold: process.env.PUBLIC_URL + "/image/icons/settings_bold.svg",
    alert_icon: process.env.PUBLIC_URL + "/image/icons/alert.svg",
    alert_icon_bold: process.env.PUBLIC_URL + "/image/icons/alert_bold.svg",
    profile_icon: process.env.PUBLIC_URL + "/image/icons/profile.svg",
    intro_icon: process.env.PUBLIC_URL + "/image/misc/intro_icon.svg",
    page_title: "Terms & Conditions",
  };


  // ── Tab ────────────────────────────────────────────────────────────────────
  const [tabValue, setTabValue] = useState<string>("compliance");

  // ── Tour Related State ───────────────────────────────────────────
  const [isTourOptionsOpen, setIsTourOptionsOpen] = useState(false);
  const [activeTour, setActiveTour] = useState<string | null>(null);
  const [currentTourSteps, setCurrentTourSteps] = useState<Step[]>([] as Step[]);
  const [tourStepIndex, setTourStepIndex] = useState(0);

  // ── Pagination — each tab has its own fully isolated state ─────────────────
  // CHANGE 1: Replaced shared `paginationFrequency` with per-tab limit states
  const [complianceLimit, setComplianceLimit] = useState<string>("20");
  const [repoLimit, setRepoLimit]             = useState<string>("20");

  const [compliancePage, setCompliancePage] = useState<number>(1);
  const [repoPage, setRepoPage]             = useState<number>(1);

  // ── Compliance filters ─────────────────────────────────────────────────────
  const [complianceSearch, setComplianceSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch]   = useState<string>("");
  const [msaFilter, setMsaFilter]               = useState<string>("all");
  const [statusFilter, setStatusFilter]         = useState<string>("all");
  const [sortBy, setSortBy]                     = useState<string>("recent");

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = useCallback((value: string) => {
    setComplianceSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (value.length === 0 || value.length >= 3) {
        setDebouncedSearch(value);
        setCompliancePage(1);
      }
    }, 400);
  }, []);

  // ── Terms Repository filters ───────────────────────────────────────────────
  const [repoStatusFilter, setRepoStatusFilter] = useState<string>("all");
  const [repoTypeFilter, setRepoTypeFilter]     = useState<string>("all");

  // ── History filters ────────────────────────────────────────────────────────
  const [historyPage, setHistoryPage]   = useState<number>(1);
  const [historyLimit, setHistoryLimit] = useState<string>("20");
  const [historyOrgFilter, setHistoryOrgFilter]   = useState<string>("all");
const [historySearch, setHistorySearch]         = useState<string>("");
const [debouncedHistorySearch, setDebouncedHistorySearch] = useState<string>("");
// Accumulate unique org options seen across history fetches



const historySearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

const handleHistorySearchChange = useCallback((value: string) => {
  setHistorySearch(value);
  if (historySearchDebounceRef.current) clearTimeout(historySearchDebounceRef.current);
  historySearchDebounceRef.current = setTimeout(() => {
    if (value.length === 0 || value.length >= 3) {
      setDebouncedHistorySearch(value);
      setHistoryPage(1);
    }
  }, 400);
}, []);



  // ── Queries ────────────────────────────────────────────────────────────────
  const {
    data: orgData,
    isLoading: isLoadingOrgs,
    isFetching: isFetchingOrgs,
  } = useOrgComplianceList({
    search:        debouncedSearch || undefined,
    contract_type: msaFilter !== "all" ? msaFilter : undefined,
    terms_status:  statusFilter !== "all" ? statusFilter : undefined,
    sort_by:       sortBy !== "recent" ? sortBy : undefined,
    current:       compliancePage,
    // CHANGE 2: was parseInt(paginationFrequency)
    limit_to:      parseInt(complianceLimit),
  });

  const {
    data: termsData,
    isLoading: isLoadingTerms,
    isFetching: isFetchingTerms,
  } = useTermsRepositoryList({
    statusFilter: repoStatusFilter,
    typeFilter:   repoTypeFilter,
    current:      repoPage,
    // CHANGE 3: was parseInt(paginationFrequency)
    limit_to:     parseInt(repoLimit),
  });

  const {
    data: historyData,
    isLoading: isLoadingHistory,
    isFetching: isFetchingHistory,
  } = useTermsHistory({
    current:  historyPage,
    limit_to: parseInt(historyLimit),
    organization: historyOrgFilter !== "all" ? historyOrgFilter : undefined,
  search:       debouncedHistorySearch || undefined,
  });

  const { data: msaDomainsData } = useMsaDomains();

  const { data: allDomainsData } = useAllDomains();
 // console.log("allDomainsData", allDomainsData);


const historyOrgOptions = (allDomainsData?.Domains ?? []).map((domain: string) => ({
  value: domain.split(".")[0],   
  label: domain.split(".")[0],                 
}));

  const historyEvents     = historyData?.events ?? [];
  const totalHistory      = historyData?.total ?? 0;
  const historyStartCount = (historyPage - 1) * parseInt(historyLimit) + 1;

 // const [historyOrgOptions, setHistoryOrgOptions] = useState<{ value: string; label: string }[]>([]);


  // ── Mutations ──────────────────────────────────────────────────────────────
  const queryClient            = useQueryClient();
  const uploadMsaMutation      = useUploadPhysicalMsa();
  const createVersionMutation  = useCreateNewTermsVersion();
  const updateVersionMutation  = useUpdateTermsVersion();
  const publishVersionMutation = usePublishTermsVersion();
  const deleteVersionMutation  = useDeleteTermsVersion();
  const duplicateVersionMutation  = useDuplicateTermsVersion();
  const deactivateMutation        = useDeactivateTermsVersion();

  const handleUnpublishVersionSubmit = (item: TermsVersionItem) => {
    deactivateMutation.mutate(item.id, {
      onSuccess: () => {
        livisAlert("Terms version unpublished successfully.", "success");
      },
      onError: (err: any) => {
        livisAlert(err?.message ?? "Failed to unpublish version.", "error");
      },
    });
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const filteredOrgs: OrgComplianceItem[] = orgData?.organizations ?? [];
  const totalOrgs = orgData?.total ?? 0;

  const filteredTerms: TermsVersionItem[] = termsData?.terms ?? [];
  const totalTerms = termsData?.total ?? 0;

  // CHANGE 4: Replaced single `limit` with per-tab limit numbers
  const complianceLimitNum = parseInt(complianceLimit);
  const repoLimitNum       = parseInt(repoLimit);

  // Compliance-specific derived counts
  // CHANGE 5: was (compliancePage - 1) * limit + 1
  const complianceStart = (compliancePage - 1) * complianceLimitNum + 1;

  // Repository-specific derived counts
  // CHANGE 6: was (repoPage - 1) * limit + 1
  const repoStart = (repoPage - 1) * repoLimitNum + 1;

  // CHANGE 7: Removed handleSelectPaginationFrequency — each tab now has its
  // own inline onChange handler directly on the Select (see JSX below)

  // ── Pagination handlers ────────────────────────────────────────────────────
  const handleComplianceNextPage = () => {
    if (complianceStart + filteredOrgs.length - 1 < totalOrgs) setCompliancePage((p) => p + 1);
  };
  const handleCompliancePrevPage = () => {
    if (compliancePage > 1) setCompliancePage((p) => p - 1);
  };

  const handleRepoNextPage = () => {
    if (repoStart + filteredTerms.length - 1 < totalTerms) setRepoPage((p) => p + 1);
  };
  const handleRepoPrevPage = () => {
    if (repoPage > 1) setRepoPage((p) => p - 1);
  };

  // ── Modal: Upload MSA ──────────────────────────────────────────────────────
  const [uploadMsaOpen, setUploadMsaOpen]         = useState<boolean>(false);
  const [selectedOrg, setSelectedOrg] = useState<OrgComplianceItem | MsaDomainItem | null>(null);
  const [uploadedMsaFile, setUploadedMsaFile]     = useState<File | null>(null);
  const [msaVersion, setMsaVersion]               = useState<string>("");
  const [msaEffectiveDate, setMsaEffectiveDate]   = useState<Dayjs | null>(null);
  const [msaTitle, setMsaTitle]                   = useState<string>("Master Service Agreement");
  const [msaNotes, setMsaNotes]                   = useState<string>("");

  const resetMsaModal = () => {
  setUploadedMsaFile(null);
  setMsaVersion("");
  setMsaEffectiveDate(null);
  setMsaTitle("Master Service Agreement");
  setMsaNotes("");
  setSelectedOrg(null);
  setUniqueMsaVersion("");
  setMsaVersionDebouncerCalled(false);
  setMsaFormErrors({});
  setShowMsaUploadError(false);
};

  // ── Modal: Create Version ──────────────────────────────────────────────────
  const [createVersionOpen, setCreateVersionOpen]       = useState<boolean>(false);
  const [newVersionTitle, setNewVersionTitle]           = useState<string>("Customer Terms and Conditions");
  const [newVersionName, setNewVersionName]             = useState<string>("");
  const [newVersionDate, setNewVersionDate]             = useState<Dayjs | null>(null);
  const newVersionScenario = "clickwrap";
  const [newVersionChangeType, setNewVersionChangeType] = useState<"material" | "minor">("material");
  const [newVersionFile, setNewVersionFile]             = useState<File | null>(null);

  // ── Modal: Preview ─────────────────────────────────────────────────────────
  const [previewOpen, setPreviewOpen]   = useState<boolean>(false);
  const [previewItem, setPreviewItem]   = useState<TermsVersionItem | null>(null);
  const [compliancePreviewOpen, setCompliancePreviewOpen] = useState<boolean>(false);
  const [compliancePreviewUrl, setCompliancePreviewUrl]   = useState<string>("");
  const [compliancePreviewTitle, setCompliancePreviewTitle] = useState<string>("");

  // ── Modal: Edit Draft ──────────────────────────────────────────────────────
  const [editVersionOpen, setEditVersionOpen]               = useState<boolean>(false);
  const [editVersionTitle, setEditVersionTitle]             = useState<string>("");
  const [editVersionName, setEditVersionName]               = useState<string>("");
  const [editVersionDate, setEditVersionDate]               = useState<string>("");
  const [editVersionScenario, setEditVersionScenario]       = useState<string>("clickwrap");
  const [editVersionChangeType, setEditVersionChangeType]   = useState<"material" | "minor">("material");
  const [editVersionFile, setEditVersionFile]               = useState<File | null>(null);

  // ── Modal: Confirm Publish / Delete / View ─────────────────────────────────
  const [publishConfirmOpen, setPublishConfirmOpen] = useState<boolean>(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen]   = useState<boolean>(false);
  const [viewDetailsOpen, setViewDetailsOpen]       = useState<boolean>(false);

  // ── Modal: Duplicate Version ───────────────────────────────────────────────
  const [duplicateVersionOpen, setDuplicateVersionOpen]             = useState<boolean>(false);
  const [duplicateVersionTitle, setDuplicateVersionTitle]           = useState<string>("");
  const [duplicateVersionName, setDuplicateVersionName]             = useState<string>("");
  const [duplicateVersionDate, setDuplicateVersionDate]             = useState<string>("");
  const [duplicateVersionScenario, setDuplicateVersionScenario]     = useState<string>("clickwrap");
  const [duplicateVersionChangeType, setDuplicateVersionChangeType] = useState<"material" | "minor">("material");

  // ── Row context menu ───────────────────────────────────────────────────────
  const [anchorEl, setAnchorEl]             = useState<null | HTMLElement>(null);
  const [menuTargetItem, setMenuTargetItem] = useState<TermsVersionItem | null>(null);

  // version uniqueness — create
const [uniqueCreateVersion, setUniqueCreateVersion]         = useState("")
const [createVersionDebouncerCalled, setCreateVersionDebouncerCalled] = useState(false)

// version uniqueness — msa upload
const [uniqueMsaVersion, setUniqueMsaVersion]               = useState("")
const [msaVersionDebouncerCalled, setMsaVersionDebouncerCalled]       = useState(false)

// inline validation errors for modals managed manually (no react-hook-form)
const [msaFormErrors, setMsaFormErrors] = useState<{ organization?: string; title?: string; version?: string; effectiveDate?: string }>({});
const [showMsaUploadError, setShowMsaUploadError] = useState(false);

const [createVersionFormErrors, setCreateVersionFormErrors] = useState<{ title?: string; version?: string; effectiveDate?: string }>({});
const [showCreateVersionUploadError, setShowCreateVersionUploadError] = useState(false);

const [editVersionFormErrors, setEditVersionFormErrors] = useState<{ title?: string; version?: string; effectiveDate?: string }>({});

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, item: TermsVersionItem) => {
    setAnchorEl(event.currentTarget);
    setMenuTargetItem(item);
  };
  const handleMenuClose = () => setAnchorEl(null);

  // ── Drag & Drop ────────────────────────────────────────────────────────────
  const [isDragOver, setIsDragOver] = useState(false);
  const handleDragOver  = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => setIsDragOver(false);

  const handleFileDrop = (e: React.DragEvent, setter: (f: File) => void) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (file.type === "application/pdf") {
      setter(file);
    } else {
      livisAlert("Please upload a PDF file only.", "warning");
    }
  };

  const openFilePicker = (setter: (f: File) => void) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf";
    input.onchange = (e: any) => {
      const file = e.target.files?.[0];
      if (file) setter(file);
    };
    input.click();
  };

  // ── Scroll to first visible error (works for both error classes + alert boxes) ──
  const scrollToFirstError = () => {
    setTimeout(() => {
      const firstError = document.querySelector(
        "[aria-invalid='true'], .livis-text-danger:not(:empty), .MuiFormHelperText-root.Mui-error, .livis-form-alert-error"
      );
      if (firstError) {
        const scrollTarget = firstError.closest(".MuiGrid-item, .livis-terms-condition-setting-modal-field-wrap") || firstError;
        scrollTarget.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 150);
  };

  // ── Submit handlers ────────────────────────────────────────────────────────

  const handleUploadMsaSubmit = () => {
    const errors: typeof msaFormErrors = {};
    if (!selectedOrg) errors.organization = t("Please select an organization") as string;
    if (!msaTitle) errors.title = t("Please enter a title") as string;
    if (!msaVersion) errors.version = t("Please enter a version") as string;
    else if (uniqueMsaVersion) errors.version = uniqueMsaVersion;
    if (!msaEffectiveDate) errors.effectiveDate = t("Please select an effective date") as string;

    setMsaFormErrors(errors);

    const fileMissing = !uploadedMsaFile;
    setShowMsaUploadError(fileMissing);

    if (Object.keys(errors).length > 0 || fileMissing) {
      if (fileMissing) livisAlert(t("Please select or drop a PDF file to upload.") as string, "warning");
      scrollToFirstError();
      return;
    }

    uploadMsaMutation.mutate(
      {
        organization:   selectedOrg!.organization,
        document_file:  uploadedMsaFile!,
        version:        msaVersion,
        effective_date: msaEffectiveDate ? msaEffectiveDate.format("YYYY-MM-DD") : "",
        title:          msaTitle,
        notes:          msaNotes || undefined,
      },
      {
        onSuccess: () => {
          livisAlert(
            `MSA uploaded successfully for ${selectedOrg!.legal_entity_name || selectedOrg!.organization}`,
            "success"
          );
          setUploadMsaOpen(false);
          resetMsaModal();
        },
        onError: (err: any) => {
          livisAlert(err?.message ?? "Failed to upload MSA.", "error");
        },
      }
    );
  };

  const handleCreateVersionSubmit = async () => {
    const errors: typeof createVersionFormErrors = {};
    if (!newVersionTitle) errors.title = t("Please enter a title") as string;
    if (!newVersionName) errors.version = t("Please enter a version name") as string;
    else if (uniqueCreateVersion) errors.version = uniqueCreateVersion;
    if (!newVersionDate) errors.effectiveDate = t("Please select an effective date") as string;

    setCreateVersionFormErrors(errors);

    const fileMissing = !newVersionFile;
    setShowCreateVersionUploadError(fileMissing);

    if (Object.keys(errors).length > 0 || fileMissing) {
      if (fileMissing) livisAlert(t("Please select or drop a PDF file to upload.") as string, "warning");
      scrollToFirstError();
      return;
    }

    try {
      const { createNewTermsVersionAPI } = await import("../../../services/terms");
      const { termsKeys } = await import("../hooks/queryKeys/termsKeys");

      const response = await createNewTermsVersionAPI({
        version:       newVersionName,
        effectiveDate: newVersionDate ? newVersionDate.format("YYYY-MM-DD") : "",
        title:         newVersionTitle,
        scenario:      newVersionScenario,
        changeType:    newVersionChangeType as "Material" | "Minor",
        file:          newVersionFile!,
      });

      if (
        response?.status === 200 ||
        response?.status === 201 ||
        response?.data?.status === 200 ||
        response?.data?.status === 201
      ) {
        livisAlert(t("New terms version created successfully.") || "", "success");
        setCreateVersionOpen(false);
        setNewVersionFile(null);
        setNewVersionTitle("Customer Terms and Conditions");
        setNewVersionName("");
        setNewVersionDate(null);
        setNewVersionChangeType("material");
        setUniqueCreateVersion("")
setCreateVersionDebouncerCalled(false)
        setCreateVersionFormErrors({})
        setShowCreateVersionUploadError(false)
        queryClient.invalidateQueries({ queryKey: termsKeys.lists() });
      } else {
        livisAlert(
          response?.data?.message ?? t("Failed to create version. Please try again."),
          "error"
        );
      }
    } catch (err: any) {
      console.error("Create version error:", err);
      livisAlert(err?.message ?? t("Failed to create version. Please try again."), "error");
    }
  };

  const handleEditVersionSubmit = () => {
    if (!menuTargetItem) return;

    const errors: typeof editVersionFormErrors = {};
    if (!editVersionTitle) errors.title = t("Please enter a title") as string;
    if (!editVersionName) errors.version = t("Please enter a version name") as string;
    if (!editVersionDate) errors.effectiveDate = t("Please select an effective date") as string;

    setEditVersionFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      scrollToFirstError();
      return;
    }

    updateVersionMutation.mutate(
      {
        id:            menuTargetItem.id,
        title:         editVersionTitle,
        effectiveDate: editVersionDate,
        scenario:      editVersionScenario,
        changeType:    editVersionChangeType as "Material" | "Minor",
        file:          editVersionFile ?? undefined,
      },
      {
        onSuccess: () => {
          livisAlert("Version updated successfully.", "success");
          setEditVersionOpen(false);
          setEditVersionFile(null);
          setEditVersionFormErrors({});
        },
      }
    );
  };

  const handlePublishVersionSubmit = () => {
    if (!menuTargetItem) return;
    publishVersionMutation.mutate(menuTargetItem.id, {
      onSuccess: () => {
        livisAlert("Terms version published successfully.", "success");
        setPublishConfirmOpen(false);
      },
    });
  };

  const handleDeleteVersionSubmit = () => {
    if (!menuTargetItem) return;
    deleteVersionMutation.mutate(menuTargetItem.id, {
      onSuccess: () => {
        livisAlert("Version deleted successfully.", "success");
        setDeleteConfirmOpen(false);
      },
    });
  };

  const handleDuplicateVersionSubmit = () => {
    if (!menuTargetItem) return;
    duplicateVersionMutation.mutate(
      {
        id:            menuTargetItem.id,
        title:         duplicateVersionTitle,
        version:       duplicateVersionName,
        effectiveDate: duplicateVersionDate,
        scenario:      duplicateVersionScenario,
        changeType:    duplicateVersionChangeType as "Material" | "Minor",
      },
      {
        onSuccess: () => {
          livisAlert("Version duplicated successfully.", "success");
          setDuplicateVersionOpen(false);
        },
      }
    );
  };

 const debouncedCheckCreateVersion = useCallback(
  debounce(async (version: string) => {
    try {
      const response = await checkUniqueVersionAPI(version, "clickwrap")
      const isUnique = response?.data?.data?.is_unique
      if (isUnique === false) {
        const msg = t("Version already exists for this scenario")
        setUniqueCreateVersion(msg)
      } else {
        setUniqueCreateVersion("")
      }
    } catch (err: any) {
      const msg = t(err?.response?.data?.message) as string || ""
      setUniqueCreateVersion(msg ? msg.charAt(0).toUpperCase() + msg.slice(1) : "")
    } finally {
      setCreateVersionDebouncerCalled(true)
    }
  }, 1000),
  [t]
)

const debouncedCheckMsaVersion = useCallback(
  debounce(async (version: string, organization?: string) => {
    try {
      const response = await checkUniqueVersionAPI(version, "msa", organization)
      const isUnique = response?.data?.data?.is_unique
      if (isUnique === false) {
        const msg = t("Version already exists for this scenario")
        setUniqueMsaVersion(msg)
      } else {
        setUniqueMsaVersion("")
      }
    } catch (err: any) {
      const msg = t(err?.response?.data?.message) as string || ""
      setUniqueMsaVersion(msg ? msg.charAt(0).toUpperCase() + msg.slice(1) : "")
    } finally {
      setMsaVersionDebouncerCalled(true)
    }
  }, 1000),
  [t]
)

  // ── Reusable upload zone ───────────────────────────────────────────────────
  const UploadZone = ({
    file,
    fallbackName,
    onDrop,
    onClick,
  }: {
    file: File | null;
    fallbackName?: string | null;
    onDrop: (e: React.DragEvent) => void;
    onClick: () => void;
  }) => (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={onDrop}
      onClick={onClick}
      sx={{
        border: "2px dashed",
        borderColor: isDragOver ? "var(--livis-border-color) !important" : "var(--livis-border-color) !important",
        borderRadius: "8px",
        p: 4,
        textAlign: "center",
        cursor: "pointer",
        backgroundColor: isDragOver ? "#eff6ff" : "transparent",
        "&hover": { borderColor: "#3b82f6" },
      }}
    >
      <UploadIconBlue />
      <Typography variant="body2" sx={{ fontWeight: "500" }}>
        <span style={{ color: "var(--livis-color-primary-blue) !important", textDecoration: "underline" }}>{t("Click to upload")}</span>{" "}
        {t("or Drag and drop")}
      </Typography>
      <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.5 }}>
        {t("Maximum file size 2 MB (PDF)")}
      </Typography>
      {(file || fallbackName) && (
        <Box sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
          <UploadFileIcon sx={{ color: "#2563eb" }} />
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {file ? file.name : fallbackName}
          </Typography>
        </Box>
      )}
    </Box>
  );

  // ── Table columns ──────────────────────────────────────────────────────────

  const complianceColumns: Column<OrgComplianceItem>[] = [
    {
      header: t("Organization") as string,
      accessor: "organization" as const,
      render: (row: OrgComplianceItem) => (
        <Typography sx={{ fontWeight: "bold", color: "#0f172a" }}>
          {row.legal_entity_name || row.organization}
        </Typography>
      ),
    },
    {
      header: t("Contract Type") as string,
      accessor: "contract_type" as const,
      render: (row: OrgComplianceItem) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {row.contract_type === "signed_msa" ? (
            <Typography sx={{ color: "#16a34a", fontWeight: "500" }}>{t("MSA Signed")}</Typography>
          ) : row.contract_type === "clickwrap" ? (
            <Typography sx={{ color: "#3b82f6", fontWeight: "500" }}>{t("Clickwrap")}</Typography>
          ) : (
            <Typography sx={{ color: "#dc2626", fontWeight: "500" }}>{t("Not Set")}</Typography>
          )}
        </Box>
      ),
    },
    {
      header: t("Last Accepted Version") as string,
      accessor: "entity_terms_version" as const,
      render: (row: OrgComplianceItem) => (
        <Typography>{row.entity_terms_version || "—"}</Typography>
      ),
    },
    {
  header: t("Accepted At") as string,
  accessor: "entity_bound_at" as const,
  render: (row: OrgComplianceItem) => {
  if (!row.entity_bound_at) return <Typography>—</Typography>;
  const formatted = convertToLocalDateTimeWithSecs(row.entity_bound_at);
  const [datePart, ...timeParts] = formatted.split(", ");
  return (
    <Box>
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>{datePart}</Typography>
      <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>{timeParts.join(", ")}</Typography>
    </Box>
  );
},
},
    {
      header: t("Compliance Status") as string,
      accessor: "terms_status" as const,
      render: (row: OrgComplianceItem) => (
        <Box
          sx={{
            display: "inline-block",
            px: 2, py: 0.5,
            borderRadius: "16px",
            fontSize: "0.85rem",
            fontWeight: "500",
            ...getTermsStatusBadgeStyles(row.terms_status),
          }}
        >
          {t(getTermsStatusLabel(row.terms_status))}
        </Box>
      ),
    },
    {
      header: t("Authorized Contact") as string,
      accessor: "entity_bound_by_email" as const,
      render: (row: OrgComplianceItem) => (
        <Typography>{row.entity_bound_by_email || "—"}</Typography>
      ),
    },
    {
      header: t("MSA File") as string,
      accessor: "active_msa_terms" as const,
      render: (row: OrgComplianceItem) => {
        const url = row.active_msa_terms?.document_url;
        if (!url) return <Typography>—</Typography>;
        const filename = url.split("/").pop()?.replace(/\.[^.]+$/, "") ?? "—";
        return (
          <Typography sx={{ fontFamily: "monospace", fontSize: "0.8rem", color: "#374151" }}>
            {filename}
          </Typography>
        );
      },
    },
    {
      header: t("MSA Status") as string,
      accessor: "id" as const,
      headerClassName: "livis-archive-settings-th--actions",
      render: (row: OrgComplianceItem) => {
        const isClickwrap  = row.contract_type === "clickwrap";
        const isMsaSigned  = row.contract_type === "signed_msa";
        const isMsaUploaded = isMsaSigned && row.msa_configured === true;

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.5, py: 0.4,
                borderRadius: "16px",
                fontSize: "0.8rem",
                fontWeight: "600",
                backgroundColor: isClickwrap ? "#f1f5f9" : isMsaUploaded ? "var(--livis-search-background-color) !important" : isMsaSigned ? "#fee2e2" : "#f1f5f9",
                color: isClickwrap ? "#64748b" : isMsaUploaded ? "#065f46" : isMsaSigned ? "#991b1b" : "#64748b",
              }}
            >
              {isClickwrap ? (
                t("MSA Not Required")
              ) : isMsaUploaded ? (
                <><CheckCircleOutlineIcon sx={{ fontSize: "0.9rem" }} /></>
              ) : isMsaSigned ? (
                t("MSA Required")
              ) : (
                t("Not Configured")
              )}
            </Box>
            {isMsaUploaded && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  if (row.active_msa_terms?.document_url) {
                    setCompliancePreviewUrl(row.active_msa_terms.document_url);
                    setCompliancePreviewTitle(row.active_msa_terms.title ?? "MSA Document");
                    setCompliancePreviewOpen(true);
                  } else {
                    livisAlert(t("No document available for preview.") as string, "warning");
                  }
                }}
                sx={{
                  borderRadius: "20px",
                  textTransform: "none",
                  borderColor: "var(--livis-border-color) !important",
                  color: "var(--livis-gray-font-color) !important",
                  fontWeight: "600",
                 // "&:hover": { borderColor: "#cbd5e1", backgroundColor: "#f8fafc" },
                }}
              >
                {t("Preview")}
              </Button>
            )}
          </Box>
        );
      },
    },
  ];

  const repoColumns: Column<TermsVersionItem>[] = [
    {
      header: t("Version") as string,
      accessor: "version" as const,
      render: (row: TermsVersionItem) => (
        <Typography sx={{ fontWeight: "bold", color: "#0f172a" }}>{row.version}</Typography>
      ),
    },
    { header: t("Title") as string,          accessor: "title" as const },
    { header: t("Effective Date") as string, accessor: "effective_date" as const },
    {
      header: t("Change Type") as string,
      accessor: "change_type" as const,
      render: (row: TermsVersionItem) => (
        <Typography sx={{ textTransform: "capitalize" }}>{t(row.change_type)}</Typography>
      ),
    },
    {
      header: t("Needs Re-acceptance") as string,
      accessor: "requires_reacceptance" as const,
      render: (row: TermsVersionItem) => <>{row.requires_reacceptance ? t("Yes") : t("No")}</>,
    },
    {
      header: t("Status") as string,
      accessor: "is_active" as const,
      render: (row: TermsVersionItem) => (
        <Box
          sx={{
            display: "inline-block",
            px: 2, py: 0.5,
            borderRadius: "16px",
            fontSize: "0.85rem",
            fontWeight: "500",
            backgroundColor: row.is_active ? "#d1fae5" : "#e2e8f0",
            color: row.is_active ? "#065f46" : "#475569",
          }}
        >
          {row.is_active ? t("Active") : t("Draft")}
        </Box>
      ),
    },
    { header: t("Created By") as string, accessor: "created_by_email" as const },
    {
      header: t("Actions") as string,
      accessor: "id" as const,
      headerClassName: "livis-archive-settings-th--actions",
      render: (row: TermsVersionItem) => (
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => { setPreviewItem(row); setPreviewOpen(true); }}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              borderColor: "var(--livis-border-color) !important",
              color: "var(--livis-gray-font-color) !important",
              fontWeight: "600",
             // "&:hover": { borderColor: "#cbd5e1", backgroundColor: "#f8fafc" },
            }}
          >
            {t("Preview")}
          </Button>
          <IconButton size="small" onClick={(e) => handleMenuOpen(e as any, row)}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const historyColumns: Column<HistoryEventItem>[] = [
    {
      header: t("User") as string,
      accessor: "user_email" as const,
      render: (row: HistoryEventItem) => (
        <Box>
          <Typography sx={{ fontWeight: "600", color: "#0f172a", fontSize: "0.875rem" }}>
            {row.user_full_name || row.user_email}
          </Typography>
          <Typography sx={{ color: "#64748b", fontSize: "0.75rem" }}>{row.user_email}</Typography>
        </Box>
      ),
    },
    {
      header: t("Organization") as string,
      accessor: "organization" as const,
      render: (row: HistoryEventItem) => (
        <Typography>{row.legal_entity_name || row.organization}</Typography>
      ),
    },
    {
      header: t("Action") as string,
      accessor: "action" as const,
      render: (row: HistoryEventItem) => (
        <Box
          sx={{
            display: "inline-block",
            px: 2, py: 0.5,
            borderRadius: "16px",
            fontSize: "0.82rem",
            fontWeight: "600",
            textTransform: "capitalize",
            backgroundColor: row.action === "accepted" ? "#d1fae5" : "#fee2e2",
            color: row.action === "accepted" ? "#065f46" : "#991b1b",
          }}
        >
          {t(row.action)}
        </Box>
      ),
    },
    {
      header: t("Version") as string,
      accessor: "terms_version" as const,
      render: (row: HistoryEventItem) => (
        <Typography sx={{ fontWeight: "500" }}>{row.terms_version}</Typography>
      ),
    },
    {
      header: t("Method") as string,
      accessor: "acceptance_method" as const,
      render: (row: HistoryEventItem) => (
        <Typography sx={{ textTransform: "capitalize", color: "#475569", fontSize: "0.85rem" }}>
          {row.acceptance_method?.replace(/_/g, " ") || "—"}
        </Typography>
      ),
    },
    {
      header: t("Contract Type") as string,
      accessor: "contract_type" as const,
      render: (row: HistoryEventItem) => (
        <Typography sx={{ textTransform: "capitalize", color: "#475569", fontSize: "0.85rem" }}>
          {row.contract_type?.replace(/_/g, " ") || "—"}
        </Typography>
      ),
    },
    {
      header: t("Date & Time") as string,
      accessor: "created_at" as const,
      render: (row: HistoryEventItem) => (
  <Typography sx={{ color: "#475569", fontSize: "0.85rem" }}>
    {row.created_at ? convertToLocalDateTimeWithSecs(row.created_at) : "—"}
  </Typography>
),
    },
  ];

  // ── Tour step definitions (per tab) ──────────────────────────────────────
  // The three tabs (Organizations Compliance / Terms Repository / Acknowledgement
  // History) render different filter bars, tables, and action buttons, so each
  // gets its own walkthrough. Table steps use comma-fallback selectors so the
  // tour never breaks when a tab is loading a skeleton, empty, or unpopulated.
  const complianceTourSteps: Step[] = [
    {
      title: `${t("Welcome to Terms & Conditions") || ""}`,
      intro: `${t(
        "Let's take a quick tour of the Organizations Compliance tab. Here you review which organizations have accepted or are pending your terms, upload master service agreements, and track contract status."
      ) || ""}`,
      element: "#terms-condition-page",
      placement: "center",
      tooltipClass: "custom-tooltip-welcome",
    },
    {
      title: `${t("Tab Navigation") || ""}`,
      intro: `${t(
        "Switch between Organizations Compliance for acceptance status, Terms Repository for managing term versions, and Acknowledgement History for an audit trail."
      ) || ""}`,
      element: ".livis-terms-condition-mgmt-tabs",
      placement: "bottom",
    },
    {
      title: `${t("Page Heading") || ""}`,
      intro: `${t("Manage organizational compliance and master service agreements.") || ""}`,
      element: ".livis-terms-condition-setting-header-info",
      placement: "bottom",
    },
    {
      title: `${t("Upload MSA") || ""}`,
      intro: `${t(
        "Click Upload MSA to attach a signed master service agreement to an organization, including its legal entity and effective date."
      ) || ""}`,
      element: ".livis-subscription-header-info .livis-create-btn",
      placement: "bottom",
    },
    {
      title: `${t("Contract & Status Filters") || ""}`,
      intro: `${t(
        "Filter the organizations list by contract type (Clickwrap or MSA Signed) and by acceptance status (Accepted, Pending, Declined)."
      ) || ""}`,
      element: ".livis-terms-condition-setting-filter-row-1",
      placement: "bottom",
    },
    {
      title: `${t("Search Organization") || ""}`,
      intro: `${t(
        "Search for an organization by name to quickly find its compliance record."
      ) || ""}`,
      element: ".livis-terms-condition-setting-filter-row-2",
      placement: "bottom",
    },
    {
      title: `${t("Compliance Table") || ""}`,
      intro: `${t(
        "Each row shows an organization's contract type, terms version, and acceptance status. Use the row menu to view details or take action."
      ) || ""}`,
      element:
        ".livis-terms-condition-setting-main-content .livis-archive-settings-table, .livis-terms-condition-setting-empty-state, .livis-terms-condition-setting-main-content",
      placement: "top",
    },
  ];

  const repositoryTourSteps: Step[] = [
    {
      title: `${t("Welcome to Terms & Conditions") || ""}`,
      intro: `${t(
        "Let's take a quick tour of the Terms Repository tab. Here you create and manage versions of your terms documents, publish them, and decide whether changes require re-acceptance."
      ) || ""}`,
      element: "#terms-condition-page",
      placement: "center",
      tooltipClass: "custom-tooltip-welcome",
    },
    {
      title: `${t("Tab Navigation") || ""}`,
      intro: `${t(
        "Switch between Organizations Compliance for acceptance status, Terms Repository for managing term versions, and Acknowledgement History for an audit trail."
      ) || ""}`,
      element: ".livis-terms-condition-mgmt-tabs",
      placement: "bottom",
    },
    {
      title: `${t("Page Heading") || ""}`,
      intro: `${t("Manage organizational compliance and terms repository") || ""}`,
      element: ".livis-terms-condition-setting-header-info",
      placement: "bottom",
    },
    {
      title: `${t("Create New Version") || ""}`,
      intro: `${t(
        "Click Create New Version to upload a new terms document, set its change type (Material or Minor), and choose whether existing users must re-accept."
      ) || ""}`,
      element: ".livis-subscription-header-info .livis-create-btn",
      placement: "bottom",
    },
    {
      title: `${t("Status & Type Filters") || ""}`,
      intro: `${t(
        "Filter the versions list by status (Active or Draft) and by change type (Material or Minor)."
      ) || ""}`,
      element: ".livis-terms-condition-setting-repo-filter-wrap",
      placement: "bottom",
    },
    {
      title: `${t("Repository Table") || ""}`,
      intro: `${t(
        "Each row is a terms version with its title, effective date, change type, and active state. Use the row menu to edit, publish, duplicate, or deactivate a version."
      ) || ""}`,
      element:
        ".livis-terms-condition-setting-main-content .livis-archive-settings-table, .livis-terms-condition-setting-empty-state, .livis-terms-condition-setting-main-content",
      placement: "top",
    },
  ];

  const historyTourSteps: Step[] = [
    {
      title: `${t("Welcome to Terms & Conditions") || ""}`,
      intro: `${t(
        "Let's take a quick tour of the Acknowledgement History tab. Here you view an audit trail of who acknowledged which terms version and when, across all organizations."
      ) || ""}`,
      element: "#terms-condition-page",
      placement: "center",
      tooltipClass: "custom-tooltip-welcome",
    },
    {
      title: `${t("Tab Navigation") || ""}`,
      intro: `${t(
        "Switch between Organizations Compliance for acceptance status, Terms Repository for managing term versions, and Acknowledgement History for an audit trail."
      ) || ""}`,
      element: ".livis-terms-condition-mgmt-tabs",
      placement: "bottom",
    },
    {
      title: `${t("Page Heading") || ""}`,
      intro: `${t("Manage organizational compliance and terms repository") || ""}`,
      element: ".livis-terms-condition-setting-header-info",
      placement: "bottom",
    },
    {
      title: `${t("Organization Filter") || ""}`,
      intro: `${t(
        "Narrow the acknowledgement history to a single organization, or view events across all organizations."
      ) || ""}`,
      element: ".livis-terms-condition-setting-filter-row-1",
      placement: "bottom",
    },
    {
      title: `${t("Search Organization") || ""}`,
      intro: `${t(
        "Search the history for a specific organization by name."
      ) || ""}`,
      element: ".livis-terms-condition-setting-filter-row-2",
      placement: "bottom",
    },
    {
      title: `${t("History Table") || ""}`,
      intro: `${t(
        "Each row is one acknowledgement event, showing the user, organization, terms version, action, and timestamp."
      ) || ""}`,
      element:
        ".livis-archive-settings-table-wrap",
      placement: "center",
    },
  ];

  // Human-readable label for whichever tab is active, shown in the floating
  // button's options menu.
  const tabTourLabel =
    tabValue === "history"
      ? t("Acknowledgement History Page Tour")
      : tabValue === "repository"
        ? t("Terms Repository Page Tour")
        : t("Organizations Compliance Page Tour");

  // Snapshots the active tab's steps into state and opens the tour. Step
  // content is fixed at start-time so it always matches the visible tab.
  const startTabTour = () => {
    const steps =
      tabValue === "history"
        ? historyTourSteps
        : tabValue === "repository"
          ? repositoryTourSteps
          : complianceTourSteps;
    if (steps.length === 0) return;
    setCurrentTourSteps(steps);
    setActiveTour(tabValue);
    setIsTourOptionsOpen(false);
  };

  // End / reset the tour
  const endTour = () => {
    setActiveTour(null);
    setCurrentTourSteps([]);
    setTourStepIndex(0);
  };

  const handleTourStart = () => {
    // hook point kept for parity with other tours (logging if needed)
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <LandingMain landing_page_props={landing_page_props}>
      <LivisBreadcrumb title={t("Terms & Conditions") as string} className="livis-breadcrumb-myprofile-layout" />

      {/* Sub Header */}
      <div id="terms-condition-page" className="livis-manage-data-image-grid-sub-header livis-archive-settings-image-grid-sub-header livis-terms-condition-setting-sub-header">
        <Grid className="livis-manage-data-image-grid-sub-header-content" container justifyContent="space-between" alignItems="center">
          <Grid item xs={12} sm={12} md={12} lg={12} container justifyContent="start" flexWrap="wrap">
            <LivisTabs
              value={tabValue}
              onChange={(_, val) => {
                setTabValue(val as string);
                setCompliancePage(1);
                setRepoPage(1);
              }}
              ariaLabel="Terms and Conditions tabs"
              className="livis-terms-condition-mgmt-tabs"
              tabs={[
                { value: "compliance", label: t("Organizations Compliance") as string },
                { value: "repository", label: t("Terms Repository") as string },
                { value: "history", label: t("Acknowledgement History") as string },
              ]}
            />
          </Grid>
        </Grid>

        <Box className="livis-subscription-header-info livis-archive-settings-header-info livis-terms-condition-setting-header-info">
          <div>
            <Typography variant="h4" className="livis-subscription-title livis-archive-settings-title livis-terms-condition-setting-title">
              {t("Terms & Conditions")}
            </Typography>
            <Typography variant="body1" className="livis-subscription-expiry livis-archive-settings-subtitle livis-terms-condition-setting-subtitle">
              {tabValue === "compliance"
                ? t("Manage organizational compliance and master service agreements.")
                : t("Manage organizational compliance and terms repository")}
            </Typography>
          </div>

          {/* Header action buttons */}
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            {tabValue === "compliance" && (
              <Button
                className="livis-create-btn"
                variant="outlined"
                onClick={() => {
                  setSelectedOrg(null);
                  resetMsaModal();
                  setUploadMsaOpen(true);
                }}
                endIcon={<UploadIconBlue size={32} />}
              >
                {t("Upload MSA")}
              </Button>
            )}

            {tabValue === "repository" && (
              <Button
                className="livis-create-btn"
                variant="outlined"
                onClick={() => {
                  setCreateVersionFormErrors({});
                  setShowCreateVersionUploadError(false);
                  setCreateVersionOpen(true);
                }}
                endIcon={<CreateNew />}
              >
                {t("Create New Version")}
              </Button>
            )}
          </Box>
        </Box>
      </div>

      {/* Main Content */}
      <Box className="livis-terms-condition-setting-main-content">
        {tabValue === "compliance" ? (
          <>
            {/* Compliance Filter Bar */}
            <Box className="livis-terms-condition-setting-filter-wrap">
              {/* Row 1: Filters (left) + Pagination (right) */}
              <Box className="livis-terms-condition-setting-filter-row-1">
                <Box className="livis-manage-user-filters-wrap" sx={{gap: "1rem !important"}}>
  <div className="livis-manage-user-filter">
    <FormControl variant="standard" className="livis-trash-part-select">
      <Select
        value={msaFilter}
        onChange={(e) => { setMsaFilter(e.target.value); setCompliancePage(1); }}
        IconComponent={(props) => <ExpandMoreIcon {...props} />}
      >
        <MenuItem className="livis-menu-item" value="all">{t("All Contract Types")}</MenuItem>
        <MenuItem className="livis-menu-item" value="clickwrap">{t("Clickwrap")}</MenuItem>
        <MenuItem className="livis-menu-item" value="signed_msa">{t("MSA Signed")}</MenuItem>
      </Select>
    </FormControl>
  </div>

  <div className="livis-manage-user-filter">
    <FormControl variant="standard" className="livis-trash-part-select">
      <Select
        value={statusFilter}
        onChange={(e) => { setStatusFilter(e.target.value); setCompliancePage(1); }}
        IconComponent={(props) => <ExpandMoreIcon {...props} />}
      >
        <MenuItem className="livis-menu-item" value="all">{t("All Status")}</MenuItem>
        <MenuItem className="livis-menu-item" value="accepted">{t("Accepted")}</MenuItem>
        <MenuItem className="livis-menu-item" value="pending">{t("Pending")}</MenuItem>
        <MenuItem className="livis-menu-item" value="declined">{t("Declined")}</MenuItem>
      </Select>
    </FormControl>
  </div>
</Box>

                <Box className="livis-terms-condition-setting-pagination-wrap">
                  <LivisPagination
                    currentPage={compliancePage}
                    pageLength={parseInt(complianceLimit)}
                    totalCount={totalOrgs}
                    startCount={complianceStart}
                    onPrev={handleCompliancePrevPage}
                    onNext={handleComplianceNextPage}
                    frequency={{
                      value: complianceLimit,
                      onChange: (value) => {
                        setComplianceLimit(value)
                        setCompliancePage(1)
                      },
                      options: ["10", "20", "50"],
                    }}
                  />
                </Box>
              </Box>

              {/* Row 2: Sort By (left) + Search (right) */}
              <Box className="livis-terms-condition-setting-filter-row-2">
                {/* <FormControl variant="standard" className="livis-terms-condition-setting-sort-control">
                  <Select
                    value={sortBy}
                    onChange={(e) => { setSortBy(e.target.value); setCompliancePage(1); }}
                    IconComponent={(props) => <ExpandMoreIcon {...props} />}
                    className="livis-terms-condition-setting-select"
                    SelectDisplayProps={{ style: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } }}
                  >
                    <MenuItem value="recent">{t("Sort By: Recent")}</MenuItem>
                    <MenuItem value="alphabetical">{t("Sort By: Organization Name")}</MenuItem>
                  </Select>
                </FormControl> */}

                <TextField
                  placeholder={t("Search Organization") as string}
                  size="small"
                  value={complianceSearch}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  InputProps={{ startAdornment: <SearchIcon className="livis-terms-condition-setting-search-icon" /> }}
                  className="livis-terms-condition-setting-search-field"
                />
              </Box>
            </Box>

            {isLoadingOrgs || isFetchingOrgs ? (
              <AuditLogSkeleton />
            ) : filteredOrgs.length === 0 ? (
              <Box className="livis-terms-condition-setting-empty-state"><NoDataTemplate type="termOrgProfile" /></Box>
            ) : (
              <LivisGeneralTable
                columns={complianceColumns}
                data={filteredOrgs}
                tableClassName="livis-archive-settings-table livis-audit-log-table"
                theadClassName="livis-archive-settings-table-head"
                trClassName="livis-archive-settings-tr"
                thClassName="livis-archive-settings-th"
                tbodyClassName="livis-archive-settings-tbody"
                rowClassName="livis-archive-settings-row"
                tdClassName="livis-archive-settings-td"
              />
            )}
          </>
        ) : tabValue === "repository" ? (
          <>
            {/* Terms Repository Filter Bar */}
            <Box className="livis-terms-condition-setting-repo-filter-wrap">
              <Box className="livis-manage-user-filters-wrap" sx={{gap: "1rem !important"}}>
  <div className="livis-manage-user-filter">
    <FormControl variant="standard" className="livis-trash-part-select">
      <Select
        value={repoStatusFilter}
        onChange={(e) => { setRepoStatusFilter(e.target.value); setRepoPage(1); }}
        IconComponent={(props) => <ExpandMoreIcon {...props} />}
      >
        <MenuItem className="livis-menu-item" value="all">{t("All Status")}</MenuItem>
        <MenuItem className="livis-menu-item" value="active">{t("Active")}</MenuItem>
        <MenuItem className="livis-menu-item" value="inactive">{t("Draft")}</MenuItem>
      </Select>
    </FormControl>
  </div>

  <div className="livis-manage-user-filter">
    <FormControl variant="standard" className="livis-trash-part-select">
      <Select
        value={repoTypeFilter}
        onChange={(e) => { setRepoTypeFilter(e.target.value); setRepoPage(1); }}
        IconComponent={(props) => <ExpandMoreIcon {...props} />}
      >
        <MenuItem className="livis-menu-item" value="all">{t("All Type")}</MenuItem>
        <MenuItem className="livis-menu-item" value="material">{t("Material")}</MenuItem>
        <MenuItem className="livis-menu-item" value="minor">{t("Minor")}</MenuItem>
      </Select>
    </FormControl>
  </div>
</Box>

              <Box className="livis-terms-condition-setting-pagination-wrap">
                <LivisPagination
                  currentPage={repoPage}
                  pageLength={parseInt(repoLimit)}
                  totalCount={totalTerms}
                  startCount={repoStart}
                  onPrev={handleRepoPrevPage}
                  onNext={handleRepoNextPage}
                  frequency={{
                    value: repoLimit,
                    onChange: (value) => {
                      setRepoLimit(value)
                      setRepoPage(1)
                    },
                    options: ["10", "20", "50"],
                  }}
                />
              </Box>
            </Box>

            {isLoadingTerms || isFetchingTerms ? (
              <AuditLogSkeleton />
            ) : filteredTerms.length === 0 ? (
              <Box className="livis-terms-condition-setting-empty-state"><NoDataTemplate type="termRepo" /></Box>
            ) : (
              <LivisGeneralTable
                columns={repoColumns}
                data={filteredTerms}
                tableClassName="livis-archive-settings-table livis-audit-log-table"
                theadClassName="livis-archive-settings-table-head"
                trClassName="livis-archive-settings-tr"
                thClassName="livis-archive-settings-th"
                tbodyClassName="livis-archive-settings-tbody"
                rowClassName="livis-archive-settings-row"
                tdClassName="livis-archive-settings-td"
              />
            )}
          </>
        ) : tabValue === "history" ? (
          <>
            {/* History Pagination Bar */}
<Box className="livis-terms-condition-setting-filter-wrap">
  {/* Row 1: Org dropdown (left) + Pagination (right) */}
  <Box className="livis-terms-condition-setting-filter-row-1">
    <Box className="livis-manage-user-filters-wrap">
  <div className="livis-manage-user-filter">
    <FormControl variant="standard" className="livis-trash-part-select">
      <Select
        value={historyOrgFilter}
        onChange={(e) => { setHistoryOrgFilter(e.target.value); setHistoryPage(1); }}
        IconComponent={(props) => <ExpandMoreIcon {...props} />}
      >
        <MenuItem className="livis-menu-item" value="all">{t("All Organizations")}</MenuItem>
        {historyOrgOptions.map((o: { value: string; label: string }) => (
          <MenuItem className="livis-menu-item" key={o.value} value={o.value}>{o.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
  </div>
</Box>

    <Box className="livis-terms-condition-setting-pagination-wrap">
      <LivisPagination
        currentPage={historyPage}
        pageLength={parseInt(historyLimit)}
        totalCount={totalHistory}
        startCount={historyStartCount}
        onPrev={() => {
          if (historyPage > 1) setHistoryPage(historyPage - 1)
        }}
        onNext={() => {
          if (historyStartCount + historyEvents.length - 1 < totalHistory)
            setHistoryPage(historyPage + 1)
        }}
        frequency={{
          value: historyLimit,
          onChange: (value) => {
            setHistoryLimit(value)
            setHistoryPage(1)
          },
          options: ["10", "20", "50"],
        }}
      />
    </Box>
  </Box>

  {/* Row 2: Search */}
  <Box className="livis-terms-condition-setting-filter-row-2">
    <TextField
      placeholder={t("Search Organization") as string}
      size="small"
      value={historySearch}
      onChange={(e) => handleHistorySearchChange(e.target.value)}
      InputProps={{ startAdornment: <SearchIcon className="livis-terms-condition-setting-search-icon" /> }}
      className="livis-terms-condition-setting-search-field"
    />
  </Box>
</Box>

            {isLoadingHistory || isFetchingHistory ? (
              <AuditLogSkeleton />
            ) : historyEvents.length === 0 ? (
              <Box className="livis-terms-condition-setting-empty-state"><NoDataTemplate type="termHistory" /></Box>
            ) : (
              <LivisGeneralTable
                columns={historyColumns}
                data={historyEvents}
                tableClassName="livis-archive-settings-table livis-audit-log-table"
                theadClassName="livis-archive-settings-table-head"
                trClassName="livis-archive-settings-tr"
                thClassName="livis-archive-settings-th"
                tbodyClassName="livis-archive-settings-tbody"
                rowClassName="livis-archive-settings-row"
                tdClassName="livis-archive-settings-td"
              />
            )}
          </>
        ) : null}
      </Box>

      {/* Row context menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {menuTargetItem && !menuTargetItem.is_active && [
          <MenuItem key="edit" onClick={() => {
            setEditVersionTitle(menuTargetItem.title);
            setEditVersionName(menuTargetItem.version);
            setEditVersionDate(menuTargetItem.effective_date);
            setEditVersionScenario(menuTargetItem.scenario);
            setEditVersionChangeType(menuTargetItem.change_type);
            setEditVersionFile(null);
            setEditVersionFormErrors({});
            setEditVersionOpen(true);
            handleMenuClose();
          }}>{t("Edit draft")}</MenuItem>,
          <MenuItem key="publish" onClick={() => { setPublishConfirmOpen(true); handleMenuClose(); }}>{t("Publish")}</MenuItem>,
          <MenuItem key="delete"  onClick={() => { setDeleteConfirmOpen(true); handleMenuClose(); }}>{t("Delete Draft")}</MenuItem>,
        ]}
        {menuTargetItem && menuTargetItem.is_active && (
          <MenuItem onClick={() => { handleUnpublishVersionSubmit(menuTargetItem); handleMenuClose(); }}>
            {t("Unpublish")}
          </MenuItem>
        )}
        <MenuItem onClick={() => { setViewDetailsOpen(true); handleMenuClose(); }}>{t("View details")}</MenuItem>
        <MenuItem onClick={() => {
          if (menuTargetItem?.document_url) {
            window.open(menuTargetItem.document_url, "_blank");
          } else {
            livisAlert(t("No document attached to this version") as string, "warning");
          }
          handleMenuClose();
        }}>{t("Download PDF")}</MenuItem>
      </Menu>

      {/* MODAL 1: Upload MSA */}
      <LivisModal
        size="medium"
        animation_type="slideup"
        modal_open={uploadMsaOpen}
        title={t("Upload MSA") as string}
        livis_dismiss_btn={() => { setUploadMsaOpen(false); resetMsaModal(); }}
        disable_outside_click={true}
        cancel_icon={true}
        footerPrimaryButton={{
          label: t("Upload") as string,
          onClick: handleUploadMsaSubmit,
          className: "livis-inspectio-result-export-download-btn livis-primary-button-margin-right",
          disabled: uploadMsaMutation.isPending
        }}
        forceAlignButton={true}
      >
        <Box className="livis-terms-condition-setting-modal-body">
          {!selectedOrg ? (
            <Box className="livis-terms-condition-setting-modal-field-wrap">
              <ErrorSelect
                label={`${t("Organization")} *`}
                value=""
                displayEmpty
                error={!!msaFormErrors.organization}
                helperText={msaFormErrors.organization}
                onChange={(e: any) => {
                  const org = (msaDomainsData?.domains ?? []).find(
                    (o: MsaDomainItem) => o.organization === e.target.value
                  );
                  if (org) {
                    setSelectedOrg(org);
                    setMsaFormErrors((prev) => ({ ...prev, organization: undefined }));
                  }
                }}
              >
                <MenuItem value="" disabled>{t("Select an organization")}</MenuItem>
                {(msaDomainsData?.domains ?? []).map((o: MsaDomainItem) => (
                  <MenuItem key={o.organization} value={o.organization}>
                    {o.legal_entity_name || o.organization}
                  </MenuItem>
                ))}
              </ErrorSelect>
            </Box>
          ) : (
            <Box className="livis-terms-condition-setting-modal-field-wrap">
              <TextField
                value={selectedOrg.legal_entity_name || selectedOrg.organization}
                disabled
                fullWidth
                variant="standard"
                className="livis-input-field"
                label={t("Organization name")}
                InputLabelProps={{ className: "livis-input-field-component-label", shrink: true }}
              />
            </Box>
          )}

          <Box className="livis-terms-condition-setting-modal-field-wrap">
            <ErrorTextField
              label={`${t("Title")} *`}
              placeholder="e.g. Master Service Agreement"
              value={msaTitle}
              error={!!msaFormErrors.title}
              helperText={msaFormErrors.title}
              onChange={(e: any) => {
                setMsaTitle(e.target.value);
                if (e.target.value) setMsaFormErrors((prev) => ({ ...prev, title: undefined }));
              }}
            />
          </Box>

          <Grid container spacing={2} className="livis-terms-condition-setting-modal-field-wrap">
            <Grid item xs={6}>
              <ErrorTextField
                label={`${t("Version")} *`}
                placeholder="e.g. v1.0"
                value={msaVersion}
                error={!!uniqueMsaVersion || !!msaFormErrors.version}
                helperText={uniqueMsaVersion || msaFormErrors.version}
                onChange={(e: any) => {
                  const val = e.target.value
                  setMsaVersion(val)
                  setUniqueMsaVersion("")
                  setMsaVersionDebouncerCalled(false)
                  setMsaFormErrors((prev) => ({ ...prev, version: undefined }))
                  if (val.trim().length >= 1) {
                    debouncedCheckMsaVersion(val.trim(), (selectedOrg as any)?.organization)
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <LivisCalendarPicker
                label={`${t("Effective Date") as string} *`}
                value={msaEffectiveDate}
                onChange={(val) => {
                  setMsaEffectiveDate(val);
                  if (val) setMsaFormErrors((prev) => ({ ...prev, effectiveDate: undefined }));
                }}
                error={!!msaFormErrors.effectiveDate}
                helperText={msaFormErrors.effectiveDate}
              />
            </Grid>
          </Grid>

          <Box className="livis-terms-condition-setting-modal-field-wrap">
            <TextField
              value={msaNotes}
              onChange={(e) => setMsaNotes(e.target.value)}
              fullWidth variant="standard" className="livis-input-field"
              label={t("Notes (optional)")} placeholder={t("Any additional notes") || ""}
              InputLabelProps={{ className: "livis-input-field-component-label", shrink: true }}
            />
          </Box>
            {showMsaUploadError && !uploadedMsaFile && (
            <Box className="livis-form-alert-error" sx={{marginBottom: "1rem"}}>
              <ErrorOutlineIcon />
              <p>{t("Please select or drop a PDF file to upload.")}</p>
            </Box>
          )}
          <Typography variant="body2" className="livis-terms-condition-setting-upload-label">
            {t("Upload PDF")}
          </Typography>
          <UploadZone
            file={uploadedMsaFile}
            onDrop={(e) => handleFileDrop(e, setUploadedMsaFile)}
            onClick={() => openFilePicker(setUploadedMsaFile)}
          />
          
        </Box>
      </LivisModal>

      {/* MODAL 2: Create New Version */}
      <LivisModal
        size="medium"
        animation_type="slideup"
        modal_open={createVersionOpen}
        title={t("Create New Version") as string}
        livis_dismiss_btn={() => setCreateVersionOpen(false)}
        disable_outside_click={true}
        cancel_icon={true}
        footerPrimaryButton={{
          label: t("Create") as string,
          onClick: handleCreateVersionSubmit,
          className: "livis-inspectio-result-export-download-btn livis-primary-button-margin-right",
          disabled: createVersionMutation.isPending
        }}
          forceAlignButton={true} 
      >
        <Box className="livis-terms-condition-setting-modal-body">
          <Box className="livis-terms-condition-setting-modal-field-wrap">
            <ErrorTextField
              label={`${t("Title")} *`}
              placeholder="e.g. Customer Terms and Conditions"
              value={newVersionTitle}
              error={!!createVersionFormErrors.title}
              helperText={createVersionFormErrors.title}
              onChange={(e: any) => {
                setNewVersionTitle(e.target.value);
                if (e.target.value) setCreateVersionFormErrors((prev) => ({ ...prev, title: undefined }));
              }}
            />
          </Box>
          <Box className="livis-terms-condition-setting-modal-field-wrap">
            {showCreateVersionUploadError && !newVersionFile && (
              <Box className="livis-form-alert-error" sx={{marginBottom: "1rem"}}>
                <ErrorOutlineIcon />
                <p>{t("Please select or drop a PDF file to upload.")}</p>
              </Box>
            )}
            <Typography variant="body2" className="livis-terms-condition-setting-upload-label">
              {t("Upload PDF")}
            </Typography>
            <UploadZone
              file={newVersionFile}
              onDrop={(e) => handleFileDrop(e, setNewVersionFile)}
              onClick={() => openFilePicker(setNewVersionFile)}
            />
            
          </Box>
          <Grid container spacing={2} className="livis-terms-condition-setting-modal-field-wrap">
            <Grid item xs={6}>
              <ErrorTextField
                label={`${t("Version name")} *`}
                placeholder="e.g. v1.0"
                value={newVersionName}
                error={!!uniqueCreateVersion || !!createVersionFormErrors.version}
                helperText={uniqueCreateVersion || createVersionFormErrors.version}
                onChange={(e: any) => {
                  const val = e.target.value
                  setNewVersionName(val)
                  setUniqueCreateVersion("")
                  setCreateVersionDebouncerCalled(false)
                  setCreateVersionFormErrors((prev) => ({ ...prev, version: undefined }))
                  if (val.trim().length >= 1) {
                    debouncedCheckCreateVersion(val.trim())
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <LivisCalendarPicker
                label={`${t("Effective Date") as string} *`}
                value={newVersionDate}
                onChange={(val) => {
                  setNewVersionDate(val);
                  if (val) setCreateVersionFormErrors((prev) => ({ ...prev, effectiveDate: undefined }));
                }}
                error={!!createVersionFormErrors.effectiveDate}
                helperText={createVersionFormErrors.effectiveDate}
              />
            </Grid>
          </Grid>

          <Box>
            <Typography variant="body2" className="livis-terms-condition-setting-upload-label">
              {t("Change Type / Requires Re-acceptance")}
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup value={newVersionChangeType} onChange={(e) => setNewVersionChangeType(e.target.value as any)}>
                <FormControlLabel value="material" control={<Radio size="small" color="primary" />} label={
                  <Box>
                    <Typography variant="body2" className="livis-terms-condition-setting-radio-label-title">
                      {t("Major/Material Update (Forces Re-acceptance)")}
                    </Typography>
                    <Typography variant="caption" className="livis-terms-condition-setting-radio-label-desc">
                      {t("Significant changes that require all users to review and accept again")}
                    </Typography>
                  </Box>
                } className="livis-terms-condition-setting-radio-item" />
                <FormControlLabel value="minor" control={<Radio size="small" />} label={
                  <Box>
                    <Typography variant="body2" className="livis-terms-condition-setting-radio-label-title">
                      {t("Minor Update (No Re-acceptance Required)")}
                    </Typography>
                    <Typography variant="caption" className="livis-terms-condition-setting-radio-label-desc">
                      {t("Small clarifications that don't materially affect the agreement")}
                    </Typography>
                  </Box>
                } className="livis-terms-condition-setting-radio-item" />
              </RadioGroup>
            </FormControl>
          </Box>
        </Box>
      </LivisModal>

      {/* MODAL 3: Preview */}
      <LivisModal
        size="medium" animation_type="slideup" modal_open={previewOpen}
        title={(previewItem?.title || t("Terms and Conditions")) as string}
        livis_dismiss_btn={() => setPreviewOpen(false)}
        disable_outside_click={true} cancel_icon={true}
        footerPrimaryButton={{
          label: t("Download") as string,
          onClick: () => {
            if (previewItem?.document_url) window.open(previewItem.document_url, "_blank");
            else livisAlert("No document URL available.", "warning");
          },
          className: "livis-modal-btn-round livis-modal-cancel-contained-btn livis-modal-cancel-contained-btn-margin",
          icon: process.env.PUBLIC_URL + "/image/icons/download.svg",
          disabled: false,
        }}
      >
        <Box className="livis-terms-condition-setting-modal-body">
          <Box className="livis-terms-condition-setting-preview-header">
            <Typography variant="h5" className="livis-terms-condition-setting-preview-title">{previewItem?.title}</Typography>
            <Typography variant="body2" className="livis-terms-condition-setting-preview-meta">Version {previewItem?.version}</Typography>
            <Typography variant="body2" className="livis-terms-condition-setting-preview-meta">Effective Date: {previewItem?.effective_date}</Typography>
          </Box>
          <Box className="livis-terms-condition-setting-preview-body">
            {previewItem?.document_url ? (
              <Box sx={{ width: "100%", height: "480px", border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
                <iframe
                  src={previewItem.document_url}
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                  title={previewItem.title}
                />
              </Box>
            ) : (
              <Typography variant="body2">{t("No document available for preview.")}</Typography>
            )}
          </Box>
        </Box>
      </LivisModal>

      {/* MODAL 4: Edit Draft */}
      <LivisModal
        size="medium" animation_type="slideup" modal_open={editVersionOpen}
        title={t("Edit Draft Version") as string}
        livis_dismiss_btn={() => setEditVersionOpen(false)}
        disable_outside_click={true} cancel_icon={true}
        footerPrimaryButton={{
          label: t("Save Changes") as string,
          onClick: handleEditVersionSubmit,
          className: "livis-modal-btn-round livis-modal-cancel-contained-btn livis-modal-cancel-contained-btn-margin",
          disabled: updateVersionMutation.isPending,
        }}
        forceAlignButton={true}
      >
        <Box className="livis-terms-condition-setting-modal-body">
          <Box className="livis-terms-condition-setting-modal-field-wrap">
            <ErrorTextField
              label={`${t("Title")} *`}
              value={editVersionTitle}
              error={!!editVersionFormErrors.title}
              helperText={editVersionFormErrors.title}
              onChange={(e: any) => {
                setEditVersionTitle(e.target.value);
                if (e.target.value) setEditVersionFormErrors((prev) => ({ ...prev, title: undefined }));
              }}
            />
          </Box>
          <Box className="livis-terms-condition-setting-modal-field-wrap">
            <Typography variant="body2" className="livis-terms-condition-setting-upload-label">{t("Replace PDF (optional)")}</Typography>
            <UploadZone file={editVersionFile} fallbackName={menuTargetItem?.document_url ? t("Existing document") as string : null} onDrop={(e) => handleFileDrop(e, setEditVersionFile)} onClick={() => openFilePicker(setEditVersionFile)} />
          </Box>
          <Grid container spacing={2} className="livis-terms-condition-setting-modal-field-wrap">
            <Grid item xs={6}>
              <ErrorTextField
                label={`${t("Version name")} *`}
                value={editVersionName}
                error={!!editVersionFormErrors.version}
                helperText={editVersionFormErrors.version}
                onChange={(e: any) => setEditVersionName(e.target.value)}
                disabled
              />
            </Grid>
            <Grid item xs={6}>
              <ErrorTextField
                type="date"
                label={`${t("Effective Date")} *`}
                value={editVersionDate}
                error={!!editVersionFormErrors.effectiveDate}
                helperText={editVersionFormErrors.effectiveDate}
                onChange={(e: any) => {
                  setEditVersionDate(e.target.value);
                  if (e.target.value) setEditVersionFormErrors((prev) => ({ ...prev, effectiveDate: undefined }));
                }}
              />
            </Grid>
          </Grid>
          <Box className="livis-terms-condition-setting-modal-field-wrap">
            <FormControl variant="standard" className="livis-input-field">
              <InputLabel className="livis-input-field-component-label" shrink>{t("Scenario")}</InputLabel>
              <Select value={editVersionScenario} onChange={(e) => setEditVersionScenario(e.target.value)} fullWidth IconComponent={(props) => <ExpandMoreIcon {...props} />}>
                <MenuItem value="clickwrap">{t("Clickwrap")}</MenuItem>
                <MenuItem value="msa_acknowledgment">{t("MSA Acknowledgment")}</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="body2" className="livis-terms-condition-setting-upload-label">{t("Change Type / Requires Re-acceptance")}</Typography>
            <FormControl component="fieldset">
              <RadioGroup value={editVersionChangeType} onChange={(e) => setEditVersionChangeType(e.target.value as any)}>
                <FormControlLabel value="material" control={<Radio size="small" />} label={<Box><Typography variant="body2" className="livis-terms-condition-setting-radio-label-title">{t("Major/Material Update (Forces Re-acceptance)")}</Typography><Typography variant="caption" className="livis-terms-condition-setting-radio-label-desc">{t("Significant changes that require all users to review and accept again")}</Typography></Box>} className="livis-terms-condition-setting-radio-item" />
                <FormControlLabel value="minor" control={<Radio size="small" />} label={<Box><Typography variant="body2" className="livis-terms-condition-setting-radio-label-title">{t("Minor Update (No Re-acceptance Required)")}</Typography><Typography variant="caption" className="livis-terms-condition-setting-radio-label-desc">{t("Small clarifications that don't materially affect the agreement")}</Typography></Box>} className="livis-terms-condition-setting-radio-item" />
              </RadioGroup>
            </FormControl>
          </Box>
        </Box>
      </LivisModal>

      {/* MODAL 5: Publish Confirmation */}
      <LivisModal size="small" animation_type="slideup" modal_open={publishConfirmOpen} title={`${t("Publish version")} - ${menuTargetItem?.version}`} livis_dismiss_btn={() => setPublishConfirmOpen(false)} disable_outside_click={true} cancel_icon={true} livis_modal_class="livis-confirm-close-modal">
        <>
          <p>{t("Are you sure you want to publish this version? It will become active and all other versions will be deactivated.")}</p>
          <Grid container className="livis-inspection-form-modal-container">
            <Button variant="outlined" size="medium" className="livis-modal-btn-round livis-modal-cancel-close-btn" onClick={() => setPublishConfirmOpen(false)}>{t("Cancel")}</Button>
            <Button variant="contained" size="medium" className="livis-modal-btn-round livis-modal-cancel-contained-btn" onClick={handlePublishVersionSubmit} disabled={publishVersionMutation.isPending}>{t("Yes, Confirm")}</Button>
          </Grid>
        </>
      </LivisModal>

      {/* MODAL 6: Delete Confirmation */}
      <LivisModal size="small" animation_type="slideup" modal_open={deleteConfirmOpen} title={`${t("Delete version")} - ${menuTargetItem?.version}`} livis_dismiss_btn={() => setDeleteConfirmOpen(false)} disable_outside_click={true} cancel_icon={true} livis_modal_class="livis-confirm-close-modal">
        <>
          <p>{t("Are you sure you want to delete this version? This action is permanent and cannot be undone.")}</p>
          <Grid container className="livis-inspection-form-modal-container">
            <Button variant="outlined" size="medium" className="livis-modal-btn-round livis-modal-cancel-close-btn" onClick={() => setDeleteConfirmOpen(false)}>{t("Cancel")}</Button>
            <Button variant="contained" size="medium" className="livis-modal-btn-round livis-modal-cancel-danger-btn" onClick={handleDeleteVersionSubmit} disabled={deleteVersionMutation.isPending}>{t("Yes, Delete")}</Button>
          </Grid>
        </>
      </LivisModal>

      {/* MODAL 7: View Details */}
<LivisModal
  size="medium"
  animation_type="slideup"
  modal_open={viewDetailsOpen}
  title={`${t("Version details")} - ${menuTargetItem?.version}`}
  livis_dismiss_btn={() => setViewDetailsOpen(false)}
  disable_outside_click={true}
  cancel_icon={true}
>
  <Box className="livis-terms-condition-setting-modal-body" sx={{padding : "0rem !important"}}>
    <Box className="livis-form-data-table-wrap-box">
      <Grid container spacing={2} className="livis-form-data-table-box-container">
        <Grid item xs={12} lg={3} md={3} sm={4}>
          <div className="livis-form-table-data">
            <Typography className="livis-form-table-data-header">
              {t("Title")}
            </Typography>
            {menuTargetItem?.title || "--"}
          </div>
        </Grid>

        <Grid item xs={12} lg={3} md={3} sm={4}>
          <div className="livis-form-table-data">
            <Typography className="livis-form-table-data-header">
              {t("Version")}
            </Typography>
            {menuTargetItem?.version || "--"}
          </div>
        </Grid>

        <Grid item xs={12} lg={3} md={3} sm={4}>
          <div className="livis-form-table-data">
            <Typography className="livis-form-table-data-header">
              {t("Effective Date")}
            </Typography>
            {menuTargetItem?.effective_date || "--"}
          </div>
        </Grid>

        <Grid item xs={12} lg={3} md={3} sm={4}>
          <div className="livis-form-table-data">
            <Typography className="livis-form-table-data-header">
              {t("Scenario")}
            </Typography>
            {menuTargetItem?.scenario || "--"}
          </div>
        </Grid>

        <Grid item xs={12} lg={3} md={3} sm={4}>
          <div className="livis-form-table-data">
            <Typography className="livis-form-table-data-header">
              {t("Change Type")}
            </Typography>
            {menuTargetItem
              ? menuTargetItem.change_type.charAt(0).toUpperCase() +
                menuTargetItem.change_type.slice(1)
              : "--"}
          </div>
        </Grid>

        <Grid item xs={12} lg={3} md={3} sm={4}>
          <div className="livis-form-table-data">
            <Typography className="livis-form-table-data-header">
              {t("Requires Re-acceptance")}
            </Typography>
            {menuTargetItem ? (menuTargetItem.requires_reacceptance ? t("Yes") : t("No")) : "--"}
          </div>
        </Grid>

        <Grid item xs={12} lg={3} md={3} sm={4}>
          <div className="livis-form-table-data">
            <Typography className="livis-form-table-data-header">
              {t("Status")}
            </Typography>
            <Typography
              className={
                menuTargetItem?.is_active
                  ? "livis-form-data-status-active"
                  : "livis-form-data-status-inactive"
              }
            >
              {menuTargetItem ? (menuTargetItem.is_active ? t("Active") : t("Draft")) : "--"}
            </Typography>
          </div>
        </Grid>

        <Grid item xs={12} lg={3} md={3} sm={4}>
          <div className="livis-form-table-data">
            <Typography className="livis-form-table-data-header">
              {t("Created By")}
            </Typography>
            {menuTargetItem?.created_by_email || "--"}
          </div>
        </Grid>

        <Grid item xs={12} lg={3} md={3} sm={4}>
          <div className="livis-form-table-data">
            <Typography className="livis-form-table-data-header">
              {t("Created At")}
            </Typography>
            {menuTargetItem?.created_at
              ? new Date(menuTargetItem.created_at).toLocaleString()
              : "--"}
          </div>
        </Grid>

        {menuTargetItem?.notes && (
          <Grid item xs={12} lg={6} md={6} sm={8}>
            <div className="livis-form-table-data">
              <Typography className="livis-form-table-data-header">
                {t("Notes")}
              </Typography>
              {menuTargetItem.notes}
            </div>
          </Grid>
        )}

        {menuTargetItem?.document_hash && (
          <Grid item xs={12}>
            <div className="livis-form-table-data">
              <Typography className="livis-form-table-data-header">
                {t("Document Hash")}
              </Typography>
              <span style={{ wordBreak: "break-all" }}>
                {menuTargetItem.document_hash}
              </span>
            </div>
          </Grid>
        )}
      </Grid>
    </Box>
  </Box>
</LivisModal>

      {/* MODAL 8: Duplicate Version */}
      <LivisModal size="medium" animation_type="slideup" modal_open={duplicateVersionOpen} title={t("Duplicate Version") as string} livis_dismiss_btn={() => setDuplicateVersionOpen(false)} disable_outside_click={true} cancel_icon={true}
        footerSecondaryButton={{ label: t("Cancel") as string, onClick: () => setDuplicateVersionOpen(false), className: "livis-modal-btn-round livis-modal-cancel-close-btn", disabled: false }}
        footerPrimaryButton={{ label: t("Duplicate") as string, onClick: handleDuplicateVersionSubmit, className: "livis-modal-btn-round livis-modal-cancel-contained-btn livis-modal-cancel-contained-btn-margin", disabled: duplicateVersionMutation.isPending }}
      >
        <Box className="livis-terms-condition-setting-modal-body">
          <Box className="livis-terms-condition-setting-modal-field-wrap">
            <TextField value={duplicateVersionTitle} onChange={(e) => setDuplicateVersionTitle(e.target.value)} fullWidth variant="standard" className="livis-input-field" label={t("Title")} InputLabelProps={{ className: "livis-input-field-component-label", shrink: true }} />
          </Box>
          <Grid container spacing={2} className="livis-terms-condition-setting-modal-field-wrap">
            <Grid item xs={6}>
              <TextField value={duplicateVersionName} onChange={(e) => setDuplicateVersionName(e.target.value)} fullWidth variant="standard" className="livis-input-field" label={t("Version name")} InputLabelProps={{ className: "livis-input-field-component-label", shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField type="date" value={duplicateVersionDate} onChange={(e) => setDuplicateVersionDate(e.target.value)} fullWidth variant="standard" className="livis-input-field" label={t("Effective Date")} InputLabelProps={{ className: "livis-input-field-component-label", shrink: true }} />
            </Grid>
          </Grid>
          <Box className="livis-terms-condition-setting-modal-field-wrap">
            <FormControl variant="standard" className="livis-input-field">
              <InputLabel className="livis-input-field-component-label" shrink>{t("Scenario")}</InputLabel>
              <Select value={duplicateVersionScenario} onChange={(e) => setDuplicateVersionScenario(e.target.value)} fullWidth IconComponent={(props) => <ExpandMoreIcon {...props} />}>
                <MenuItem value="clickwrap">{t("Clickwrap")}</MenuItem>
                <MenuItem value="msa_acknowledgment">{t("MSA Acknowledgment")}</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <FormControl variant="standard" className="livis-input-field">
              <InputLabel className="livis-input-field-component-label" shrink>{t("Change Type")}</InputLabel>
              <Select value={duplicateVersionChangeType} onChange={(e) => setDuplicateVersionChangeType(e.target.value as any)} fullWidth IconComponent={(props) => <ExpandMoreIcon {...props} />}>
                <MenuItem value="material">{t("Material (Forces Re-acceptance)")}</MenuItem>
                <MenuItem value="minor">{t("Minor (No Re-acceptance)")}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </LivisModal>

      {/* MODAL 9: Compliance MSA Preview */}
      <LivisModal
        size="large"
        animation_type="slideup"
        modal_open={compliancePreviewOpen}
        title={compliancePreviewTitle}
        livis_dismiss_btn={() => setCompliancePreviewOpen(false)}
        disable_outside_click={true}
        cancel_icon={true}
        footerPrimaryButton={{
          label: t("Download") as string,
          onClick: () => window.open(compliancePreviewUrl, "_blank"),
          className: "livis-modal-btn-round livis-modal-cancel-contained-btn livis-modal-cancel-contained-btn-margin",
          disabled: false,
        }}
      >
        <Box sx={{ width: "100%", height: "520px", border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
          {compliancePreviewUrl ? (
            <iframe
              src={compliancePreviewUrl}
              width="100%"
              height="100%"
              style={{ border: "none" }}
              title={compliancePreviewTitle}
            />
          ) : (
            <Typography variant="body2" sx={{ p: 2 }}>{t("No document available for preview.")}</Typography>
          )}
        </Box>
      </LivisModal>

      {/* ── Tour related jsx ────────────────────────────────────── */}
      <LivisFloatingButton
        icon={<LivisTourIcon />}
        label={`${t("LIVIS Tour") || ""}`}
        options={[
          { label: tabTourLabel, onClick: startTabTour },
        ]}
        isOpen={isTourOptionsOpen}
        onOpen={() => setIsTourOptionsOpen(true)}
        onClose={() => setIsTourOptionsOpen(false)}
      />

      {/* SINGLE JoyRide component for the Terms & Conditions tours */}
      <LivisTourWrapper
        steps={currentTourSteps}
        tourKey={activeTour || ""}
        isOpen={!!activeTour}
        onTourEnd={endTour}
        onTourStart={handleTourStart}
        continuous={false}
        showProgress={true}
        showSkipButton={true}
        spotlightClicks={false}
      />

    </LandingMain>
  );
};

export default TermsAndConditionsSettings;